import React, { Component } from 'react';

import onClickOutside from 'react-onclickoutside';
import debounce from 'lodash/debounce';

import { Project } from 'tstypes';
import SearchResult from './components/SearchResult';

import {
  SSearchInput,
  SPlaceholder,
  SContainer,
  SSearchIcon,
  SSearchInputContainer,
  SHotKeys,
} from './styled';

const keyMap = {
  activateSearch: 'alt+j',
  leave: ['esc', 'escape'],
};

type Item = {
  header: string;
  id: string;
  name: string;
  nameIsHighlightable: boolean;
  secondaryInfoText: string;
  icon: string;
  count: string;
  xpack?: string;
  subUrl: string;
};

interface Props {
  currentProject: Project;
  isLoaded: boolean;
  isLoading: boolean;
  items: Array<Item>;
  getItems: (arg: object) => void;
  resetState: () => void;
  onPushToHistory: (arg: string) => void;
}

export class HeaderSearch extends Component<Props> {
  getItems: any;
  inputRef: React.RefObject<HTMLInputElement>;

  constructor(props: Props) {
    super(props);
    this.getItems = debounce(this.handleSearch, 200);
    this.inputRef = React.createRef();
  }

  state = {
    isActive: false,
    value: '',
  };

  handleFocus = () => {
    if (!this.state.isActive) {
      this.setState(
        {
          isActive: true,
        },
        () => {
          if (this.inputRef.current) {
            this.inputRef.current.focus();
          }
        },
      );
    }
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.setState(
      {
        value,
      },
      () => {
        this.getItems(value);
      },
    );
  };

  handleSearch = (searchString = '') => {
    if (searchString) {
      this.props.getItems({
        query: searchString,
      });
    }

    if (!searchString && this.state.isActive) {
      this.props.resetState();
    }
  };

  handleClickOutside = () => {
    this.setState(
      {
        isActive: false,
        value: '',
      },
      () => this.handleSearch(),
    );
  };

  handleSelectItem = (subUrl: Item['subUrl']) => {
    const { account, slug } = this.props.currentProject;

    this.props.onPushToHistory(`/${account}/${slug}/${subUrl}`);
    this.setState({
      isActive: false,
      value: '',
    });
  };

  keyHandlers: { [key: string]: (keyEvent?: KeyboardEvent) => void } = {
    leave: () => this.handleEsc(),
    activateSearch: e => {
      e!.preventDefault();

      this.handleActivateSearch();
    },
  };

  handleActivateSearch = () => {
    this.handleFocus();
  };

  handleEsc = () => {
    if (this.state.value === '') {
      this.setState({
        isActive: false,
      });
    } else {
      this.setState(
        {
          value: '',
        },
        () => this.handleSearch(),
      );
    }
  };

  handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.handleEsc();
    }
  };

  render() {
    const { isActive, value } = this.state;
    const { items, isLoading, isLoaded } = this.props;

    const placeholder = 'Global Search Engine';

    return (
      <SHotKeys
        focused
        attach={window}
        handlers={this.keyHandlers}
        keyMap={keyMap}
      >
        <SContainer onClick={this.handleFocus}>
          <SSearchInputContainer isActive={isActive}>
            <SSearchInput
              ref={this.inputRef}
              autoComplete="off"
              data-test="search-input"
              name="search"
              placeholder={placeholder}
              type="search"
              value={value}
              onChange={this.handleChange}
              onKeyDown={this.handleKeyDown}
            />

            <SSearchIcon />
          </SSearchInputContainer>

          <SPlaceholder isActive={isActive}>
            <i className="icon-search" />

            {placeholder}
          </SPlaceholder>

          {value && (
            <SearchResult
              isLoaded={isLoaded}
              isLoading={isLoading}
              items={items}
              value={value}
              onSelect={this.handleSelectItem}
            />
          )}
        </SContainer>
      </SHotKeys>
    );
  }
}

export default onClickOutside(HeaderSearch);
